import { boot } from 'quasar/wrappers'
import { reactive } from 'vue'
import AiCreditBadge from '../components/ai/AiCreditBadge.vue'
import AiUsageInfo from '../components/ai/AiUsageInfo.vue'
import qbankConfig from '../config/qbank.js'

/**
 * Global reactive AI credit state.
 * Updated on app boot and after every AI request that returns a usage_info payload.
 *
 * Safari 12 / iOS 12 compat: NO optional chaining (?.), NO nullish coalescing (??).
 */
export const aiCreditsState = reactive({
    loaded: false,
    balanceJod: 0,
    lifetimePurchasedJod: 0,
    lifetimeSpentJod: 0,
    minimumChargeJod: 0.001,
    topUpUrl: '/checkout?type=ai-credit',
    features: {}, // { card_generation: { free_used, free_limit, free_remaining, multiplier }, ... }
    // WAVE H5 — the wallet's own deadline. The server has sent this since H1;
    // dropping it here meant the badge, the one credit surface a student sees on
    // every page, could not tell them their credit was about to lapse.
    expiry: {
        enabled: false,
        expiresAt: null,      // ISO string, or null when nothing can expire
        warnFromDays: 60,     // the widest rung of the SERVER's ladder, never a local guess
        inactivityMonths: 24,
    },
})

function pickValue(obj, key, fallback) {
    if (obj && obj[key] !== undefined && obj[key] !== null) {
        return obj[key]
    }
    return fallback
}

/**
 * Apply a server payload (from /api/quiz-app/ai/credit-status) to the local state.
 */
export function applyCreditStatus(data) {
    if (!data) return
    aiCreditsState.loaded = true
    aiCreditsState.balanceJod = parseFloat(pickValue(data, 'balance_jod', 0)) || 0
    aiCreditsState.lifetimePurchasedJod = parseFloat(pickValue(data, 'lifetime_purchased_jod', 0)) || 0
    aiCreditsState.lifetimeSpentJod = parseFloat(pickValue(data, 'lifetime_spent_jod', 0)) || 0
    aiCreditsState.minimumChargeJod = parseFloat(pickValue(data, 'minimum_charge_jod', 0.001)) || 0.001
    aiCreditsState.topUpUrl = pickValue(data, 'top_up_url', '/checkout?type=ai-credit')
    aiCreditsState.features = pickValue(data, 'features', {})

    // Safari 12 / iOS 12: no optional chaining, no nullish coalescing.
    const exp = pickValue(data, 'expiry', null)
    if (exp) {
        aiCreditsState.expiry = {
            enabled: !!pickValue(exp, 'enabled', false),
            expiresAt: pickValue(exp, 'expires_at', null),
            warnFromDays: parseInt(pickValue(exp, 'warn_from_days', 60), 10) || 60,
            inactivityMonths: parseInt(pickValue(exp, 'inactivity_months', 24), 10) || 24,
        }
    }
}

/**
 * Apply an inline usage_info payload returned by an AI endpoint after a successful call.
 * Updates the per-feature counters and the wallet balance in one shot.
 */
export function applyUsageInfo(usageInfo) {
    if (!usageInfo) return
    const feature = usageInfo.feature
    if (feature && aiCreditsState.features[feature]) {
        aiCreditsState.features[feature].free_used = parseInt(pickValue(usageInfo, 'free_used', 0), 10)
        aiCreditsState.features[feature].free_limit = parseInt(pickValue(usageInfo, 'free_limit', 0), 10)
        aiCreditsState.features[feature].free_remaining = parseInt(pickValue(usageInfo, 'free_remaining', 0), 10)
    } else if (feature) {
        aiCreditsState.features[feature] = {
            free_used: parseInt(pickValue(usageInfo, 'free_used', 0), 10),
            free_limit: parseInt(pickValue(usageInfo, 'free_limit', 0), 10),
            free_remaining: parseInt(pickValue(usageInfo, 'free_remaining', 0), 10),
            multiplier: 0,
        }
    }
    aiCreditsState.balanceJod = parseFloat(pickValue(usageInfo, 'balance_jod', aiCreditsState.balanceJod)) || 0
}

/**
 * Fetch the latest credit status from the server.
 * Server now returns 200 with { authenticated: false } for guests, so no try/catch
 * is needed — unauth visits don't produce Sentry noise.
 */
export async function refreshCreditStatus(axios) {
    if (!axios) return null
    const res = await axios.get('/api/quiz-app/ai/credit-status')
    const body = res && res.data ? res.data : null
    if (body && body.success && body.data) {
        applyCreditStatus(body.data)
        return body.data
    }
    return null
}

export default boot(function (params) {
    const app = params.app
    const axios = params.app && params.app.config && params.app.config.globalProperties
        ? params.app.config.globalProperties.$axios
        : null

    // Register components globally so any page can drop <AiCreditBadge /> / <AiUsageInfo />
    app.component('AiCreditBadge', AiCreditBadge)
    app.component('AiUsageInfo', AiUsageInfo)

    // Expose globally so .vue components can read via $aiCredits
    app.config.globalProperties.$aiCredits = aiCreditsState
    app.config.globalProperties.$refreshAiCredits = function () {
        return refreshCreditStatus(axios)
    }
    app.config.globalProperties.$applyAiUsageInfo = applyUsageInfo

    // Auto-apply any usage_info field returned by AI endpoints — keeps the badge fresh
    // without each AI call site having to remember to call $applyAiUsageInfo manually.
    if (axios && axios.interceptors && axios.interceptors.response) {
        axios.interceptors.response.use(function (response) {
            try {
                const data = response && response.data ? response.data : null
                if (data && data.usage_info) {
                    applyUsageInfo(data.usage_info)
                }
                // If an AI endpoint returned a balance_jod hint (e.g., on insufficient_credits)
                if (data && data.balance_jod !== undefined && data.balance_jod !== null) {
                    aiCreditsState.balanceJod = parseFloat(data.balance_jod) || 0
                }
            } catch (e) {
                // Swallow — never break the response chain
            }
            return response
        })
    }

    // Initial load (best-effort, non-blocking). Prefer the bootstrap aggregator
    // slice (populated during the qbank-config boot, which runs first) so we avoid
    // a separate /ai/credit-status request; fall back to fetching it if the
    // bootstrap path didn't run. Safari 12 safe (no optional chaining / nullish).
    const bootCredits = qbankConfig.getBootstrapAiCredits ? qbankConfig.getBootstrapAiCredits() : null
    if (bootCredits) {
        if (bootCredits.data) {
            applyCreditStatus(bootCredits.data)
        }
    } else {
        refreshCreditStatus(axios)
    }
})
